import { Component, OnDestroy, OnInit } from '@angular/core'
import { WebViewService } from '@se-po/shared-data-access-services'
import { Observable, Subject, defer, of, timer } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import {
  FinancialObligation,
  FinancialObligationList,
  PaginationSettings,
} from '../models'
import { ColumnSort } from '../models/column-sort.interface'
import {
  FinancialObligationQuery,
  FinancialObligationService,
} from '../services/financial-obligation.service'
import { SpinnerService } from '../services/spinner.service'

interface QueryChange {
  source: 'load' | 'pagination' | 'search' | 'sort'
  value?: string
  debounced: boolean
}

interface BillsLoadSuccess {
  bills: FinancialObligationList
  failed: false
}

interface BillsLoadFailure {
  failed: true
}

type BillsLoadResult = BillsLoadSuccess | BillsLoadFailure

@Component({
  selector: 'app-bills-list',
  templateUrl: './bills-list.component.html',
  standalone: false,
})
export class BillsListComponent implements OnInit, OnDestroy {
  public bills: FinancialObligation[] = []
  public pageMetadata: FinancialObligationList['metadata'] | undefined
  public loading = true
  public failed = false
  public searchTerm: string
  public displayDiscounts = false
  public displayCredits = false
  public displayHeader = false

  private queryChanged = new Subject<QueryChange>()
  private destroy$ = new Subject<void>()
  private hasLoaded = false
  private _sortedColumn: ColumnSort = { column: 'display_priority', sort: 'asc' }
  private _paginationSettings: PaginationSettings = { page: 1, per_page: 25 }

  constructor(
    private financialObligationService: FinancialObligationService,
    private spinner: SpinnerService,
    private webViewService: WebViewService
  ) { }

  public ngOnInit(): void {
    this.displayHeader = !this.webViewService.current
    this.queryChanged.pipe(
      distinctUntilChanged((previous, current) =>
        current.source !== 'load' &&
        previous.source === current.source &&
        previous.value === current.value
      ),
      switchMap(change => (change.debounced ? timer(500) : of(null)).pipe(
        map(() => this.queryParams()),
        switchMap(params => this.findBills(params))
      )),
      takeUntil(this.destroy$)
    ).subscribe(result => this.updateResults(result))

    this.loadResults()
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.queryChanged.complete()
  }

  public get paginationSettings(): PaginationSettings {
    return this._paginationSettings
  }

  public set paginationSettings(value: PaginationSettings) {
    if (
      this._paginationSettings.page === value.page &&
      this._paginationSettings.per_page === value.per_page
    ) return

    const perPageChanged = this._paginationSettings.per_page !== value.per_page
    this._paginationSettings = {
      ...value,
      page: perPageChanged ? 1 : value.page,
    }
    this.queryChanged.next({
      source: 'pagination',
      value: `${this._paginationSettings.page}:${this._paginationSettings.per_page}`,
      debounced: false,
    })
  }

  public get sortedColumn(): ColumnSort {
    return this._sortedColumn
  }

  public set sortedColumn(value: ColumnSort) {
    this._sortedColumn = value
    this.resetPage()
    this.queryChanged.next({
      source: 'sort',
      value: `${value.column}:${value.sort}`,
      debounced: true,
    })
  }

  public search(term: string): void {
    this.searchTerm = term
    this.resetPage()
    this.queryChanged.next({
      source: 'search',
      value: term,
      debounced: true,
    })
  }

  public loadResults(): void {
    this.queryChanged.next({ source: 'load', debounced: false })
  }

  private resetPage(): void {
    this._paginationSettings = { ...this._paginationSettings, page: 1 }
  }

  private queryParams(): FinancialObligationQuery {
    const params: FinancialObligationQuery = { ...this.paginationSettings }
    if (this.searchTerm && this.searchTerm.length > 1) {
      params.search_term = this.searchTerm
    }

    if (this.sortedColumn?.column) {
      params[`order[${this.sortedColumn.column}]`] = this.sortedColumn.sort
    }
    return params
  }

  private findBills(params: FinancialObligationQuery): Observable<BillsLoadResult> {
    return defer(() => {
      this.loading = !this.hasLoaded || this.failed
      this.failed = false
      this.spinner.start()

      return this.financialObligationService.mine(params).pipe(
        map(bills => ({ bills, failed: false as const })),
        catchError(() => of({ failed: true as const })),
        finalize(() => this.spinner.stop())
      )
    })
  }

  private updateResults(result: BillsLoadResult): void {
    this.hasLoaded = true
    this.failed = result.failed
    if (result.failed === true) {
      this.bills = []
      this.pageMetadata = undefined
      this.displayDiscounts = false
      this.displayCredits = false
      this.loading = false
      return
    }

    this.bills = result.bills
    this.displayDiscounts = this.bills.some(bill => bill.has_discounts)
    this.displayCredits = this.bills.some(bill => bill.has_credits)
    this.pageMetadata = result.bills.metadata
    const currentPage = this.pageMetadata?.pagination.current_page
    if (currentPage) {
      this._paginationSettings = {
        ...this._paginationSettings,
        page: currentPage,
      }
    }
    this.loading = false
  }
}
